/**
 * O campo Recurso de um ticket existente (spec 032, §4), onde a integração
 * do SGR é deste tenant: busca ao vivo pela placa, e escolher LIGA o recurso.
 *
 * - slot VAZIO na ficha efetiva é preenchido;
 * - slot com valor fica como está — o cartão mostra "No SGR: X" com "usar":
 *   nada é sobrescrito sem clique (a pessoa vence);
 * - o veículo de outro cliente cai na regra `equipment_not_of_client`, com a
 *   mensagem "este veículo é de X no SGR; o atendimento é de Y" — e nada muda.
 *
 * Ticket espelhado ou sem `tickets.edit` não chegam aqui (o campo vira texto).
 */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import type { TicketDetail, TicketSheetSlot } from '@erp/shared';
import { Spinner } from '@/components/ui/misc';
import { useTenantProfile } from '@/shared/tenant-profile';
import { usePatchTicket } from '@/features/tickets/tickets-mutations';
import { errorMessage } from '@/features/tickets/ticket-format';
import { resolveSheetOf } from '@/features/tickets/ticket-sheet-view';
import { SgrVehicleSearch } from './SgrVehicleSearch';
import { sgrPick, useSgr } from './sgr-queries';
import {
  equipmentOfOtherClientText,
  existingTicketPickPlan,
  pickedEquipmentFits,
  pickFailedText,
  sgrLinkLabels,
} from './sgr-view';

export function SgrRecursoField({ ticket }: { ticket: TicketDetail }) {
  const sgr = useSgr();
  const terms = useTenantProfile().terms;
  const patch = usePatchTicket(ticket.ticketNumber);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ text: string; tone: 'info' | 'warn' } | null>(null);
  const isSgrVehicle = (ticket.endCustomer ?? null) !== null;

  const pick = async (sgrId: string) => {
    setNote(null);
    setBusy(true);
    const res = await sgrPick(sgrId);
    setBusy(false);
    if (res.status !== 'ok') {
      setNote({
        text: pickFailedText(res, sgr.sourceName, terms, false),
        tone: 'warn',
      });
      return;
    }
    if (!pickedEquipmentFits(res.equipment, ticket.client.id)) {
      setNote({
        text: equipmentOfOtherClientText(res.equipment.clientName, ticket.client.name, terms, sgr.sourceName),
        tone: 'warn',
      });
      return;
    }
    const resolved = resolveSheetOf(ticket);
    const effective = Object.fromEntries(
      Object.entries(resolved.slots).map(([slot, r]) => [slot, r.value])
    ) as Record<TicketSheetSlot, string | null>;
    const plan = existingTicketPickPlan(effective, res.sheet);
    patch.mutate(
      {
        equipmentId: res.equipment.id,
        ...(Object.keys(plan.fill).length > 0 ? { sheet: plan.fill } : {}),
        sheetFromSgr: res.receipt,
      },
      {
        onSuccess: () => {
          setText('');
          setNote(
            plan.differs.length > 0
              ? {
                  text: `${plan.differs.length === 1 ? '1 campo da ficha já tinha valor' : `${plan.differs.length} campos da ficha já tinham valor`} — veja "No ${sgr.sourceName}" na ficha para usar.`,
                  tone: 'info',
                }
              : null
          );
        },
        onError: (e) => setNote({ text: errorMessage(e), tone: 'warn' }),
      }
    );
  };

  return (
    <div className="space-y-1.5">
      {ticket.equipment ? (
        <p className="flex items-center gap-2 text-sm">
          {isSgrVehicle ? (
            <Link
              to="/resources/$resourceId"
              params={{ resourceId: String(ticket.equipment.id) }}
              className="font-medium text-primary hover:underline"
              title={sgrLinkLabels(terms).vehicle}
            >
              {ticket.equipment.name}
            </Link>
          ) : (
            <span className="font-medium">{ticket.equipment.name}</span>
          )}
          <button
            type="button"
            className="text-xs text-muted-foreground hover:underline"
            disabled={patch.isPending}
            onClick={() => patch.mutate({ equipmentId: null })}
          >
            remover
          </button>
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">Sem recurso</p>
      )}
      <SgrVehicleSearch
        id={`recurso-${ticket.ticketNumber}`}
        value={text}
        onText={setText}
        onPick={(item) => void pick(item.sgrId)}
        sourceName={sgr.sourceName}
        terms={terms}
        placeholder={`Buscar no ${sgr.sourceName} pela ${terms.resourceId}`}
        disabled={busy || patch.isPending}
      />
      {busy || patch.isPending ? (
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <Spinner className="size-3.5" />
          {`Lendo no ${sgr.sourceName}…`}
        </p>
      ) : null}
      {note ? (
        <p role="status" className={note.tone === 'warn' ? 'text-xs text-amber-700 dark:text-amber-300' : 'text-xs text-muted-foreground'}>
          {note.text}
        </p>
      ) : null}
    </div>
  );
}
